import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  BadgeCheck,
  Ban,
  CalendarPlus,
  Download,
  Landmark,
  LoaderCircle,
  ReceiptText,
  RefreshCw,
  ShieldAlert,
  Undo2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import {
  adminSessionStatus,
  confirmBillingPaymentAdmin,
  generateBillingRenewalsAdmin,
  issueBillingInvoiceAdmin,
  listBillingInvoices,
  recordBillingRefundAdmin,
  voidBillingInvoiceAdmin,
} from "@/lib/admin.functions";
import { billingPlanName } from "@/lib/billing.catalog";
import { downloadBillingCreditNotePdf, downloadBillingInvoicePdf } from "@/lib/billing-pdf";
import {
  formatBillingPeriod,
  formatBillingPrice,
  type BillingInvoiceSummary,
  type BillingSettings,
} from "@/lib/billing.types";

export const Route = createFileRoute("/admin-rechnungen")({
  staticData: { sitemap: false, toasts: true },
  ssr: false,
  component: AdminBillingPage,
  head: () => ({
    meta: [
      { title: "Begner Admin · Rechnungen" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const statusLabels = {
  draft: "Entwurf",
  open: "Offen",
  paid: "Bezahlt",
  overdue: "Überfällig",
  void: "Storniert",
  refunded: "Erstattet",
} as const;

function statusVariant(status: BillingInvoiceSummary["status"]) {
  if (status === "paid") return "default" as const;
  if (status === "draft" || status === "open") return "secondary" as const;
  return "outline" as const;
}

function AdminBillingPage() {
  const checkSession = useServerFn(adminSessionStatus);
  const fetchInvoices = useServerFn(listBillingInvoices);
  const issueInvoice = useServerFn(issueBillingInvoiceAdmin);
  const confirmPayment = useServerFn(confirmBillingPaymentAdmin);
  const voidInvoiceRequest = useServerFn(voidBillingInvoiceAdmin);
  const generateRenewals = useServerFn(generateBillingRenewalsAdmin);
  const recordRefund = useServerFn(recordBillingRefundAdmin);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoiceSummary[]>([]);
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<BillingInvoiceSummary | null>(null);
  const [voidInvoice, setVoidInvoice] = useState<BillingInvoiceSummary | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [generatingRenewals, setGeneratingRenewals] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState<BillingInvoiceSummary | null>(null);
  const [refundedAt, setRefundedAt] = useState("");
  const [refundReference, setRefundReference] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [receivedAt, setReceivedAt] = useState("");
  const [bankReference, setBankReference] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const session = await checkSession();
      setAuthenticated(session.authenticated);
      if (!session.authenticated) return;
      const result = await fetchInvoices();
      setInvoices(result.invoices);
      setSettings(result.settings);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Rechnungen konnten nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [checkSession, fetchInvoices]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(
    () => ({
      draft: invoices.filter((invoice) => invoice.status === "draft").length,
      open: invoices.filter((invoice) => invoice.status === "open" || invoice.status === "overdue")
        .length,
      paid: invoices.filter((invoice) => invoice.status === "paid").length,
    }),
    [invoices],
  );

  if (authenticated === null || loading) {
    return (
      <div className="container-page flex min-h-[60vh] items-center justify-center gap-3 py-20 text-muted-foreground">
        <LoaderCircle className="size-5 animate-spin" /> Rechnungen werden geladen.
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="container-page flex min-h-[65vh] items-center justify-center py-16">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Admin-Anmeldung erforderlich</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Melden Sie sich zuerst im geschützten Adminbereich an.
            </p>
            <Button asChild className="mt-5 w-full">
              <Link to="/admin">Zum Adminbereich</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  async function handleIssue(invoice: BillingInvoiceSummary) {
    setBusyId(invoice.id);
    try {
      const result = await issueInvoice({ data: { invoiceId: invoice.id } });
      setInvoices((current) =>
        current.map((item) => (item.id === result.invoice.id ? result.invoice : item)),
      );
      toast.success(`${result.invoice.invoiceNumber} wurde ausgestellt.`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Rechnung konnte nicht ausgestellt werden.",
      );
    } finally {
      setBusyId(null);
    }
  }

  function openPaymentDialog(invoice: BillingInvoiceSummary) {
    const local = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 16);
    setReceivedAt(local);
    setBankReference("");
    setNotes("");
    setPaymentInvoice(invoice);
  }

  async function handlePayment() {
    if (!paymentInvoice || !receivedAt) return;
    setBusyId(paymentInvoice.id);
    try {
      await confirmPayment({
        data: {
          invoiceId: paymentInvoice.id,
          amountMinor: paymentInvoice.amountMinor,
          receivedAt: new Date(receivedAt).toISOString(),
          externalReference: bankReference.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      });
      toast.success("Zahlung bestätigt und Leistung freigeschaltet.");
      setPaymentInvoice(null);
      await load();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Zahlung konnte nicht bestätigt werden.",
      );
    } finally {
      setBusyId(null);
    }
  }

  async function handleVoid() {
    if (!voidInvoice || voidReason.trim().length < 3) return;
    setBusyId(voidInvoice.id);
    try {
      const result = await voidInvoiceRequest({
        data: { invoiceId: voidInvoice.id, reason: voidReason.trim() },
      });
      setInvoices((current) =>
        current.map((item) => (item.id === result.invoice.id ? result.invoice : item)),
      );
      toast.success(`${result.invoice.invoiceNumber || "Der Entwurf"} wurde storniert.`);
      setVoidInvoice(null);
      setVoidReason("");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Rechnung konnte nicht storniert werden.",
      );
    } finally {
      setBusyId(null);
    }
  }

  async function handleGenerateRenewals() {
    setGeneratingRenewals(true);
    try {
      const result = await generateRenewals();
      toast.success(
        result.count === 1
          ? "Eine fällige Folgerechnung wurde erstellt."
          : `${result.count} fällige Folgerechnungen wurden erstellt.`,
      );
      if (result.count > 0) await load();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Folgerechnungen konnten nicht erstellt werden.",
      );
    } finally {
      setGeneratingRenewals(false);
    }
  }

  function openRefundDialog(invoice: BillingInvoiceSummary) {
    const local = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
      .toISOString()
      .slice(0, 16);
    setRefundedAt(local);
    setRefundReference("");
    setRefundReason("");
    setRefundInvoice(invoice);
  }

  async function handleRefund() {
    if (!refundInvoice || !refundedAt || refundReason.trim().length < 3) return;
    setBusyId(refundInvoice.id);
    try {
      const result = await recordRefund({
        data: {
          invoiceId: refundInvoice.id,
          refundedAt: new Date(refundedAt).toISOString(),
          externalReference: refundReference.trim() || undefined,
          reason: refundReason.trim(),
        },
      });
      setInvoices((current) =>
        current.map((item) => (item.id === result.invoice.id ? result.invoice : item)),
      );
      toast.success(`${result.invoice.creditNoteNumber} wurde erstellt und verbucht.`);
      setRefundInvoice(null);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Gutschrift konnte nicht erstellt werden.",
      );
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main className="container-page space-y-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-3 mb-3">
            <Link to="/admin">
              <ArrowLeft className="size-4" /> Formularanfragen
            </Link>
          </Button>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand-deep">
            Manuelle Abrechnung
          </p>
          <a href="/admin-rabattcodes" className="text-sm font-semibold text-primary underline">
            Rabattcodes verwalten
          </a>
          <h1 className="mt-2 font-display text-3xl font-bold">Rechnungen & Zahlungseingänge</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" disabled={!settings?.billingEnabled || generatingRenewals}>
                <CalendarPlus className="size-4" /> Fällige Folgerechnungen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Monatslauf jetzt starten?</AlertDialogTitle>
                <AlertDialogDescription>
                  Für alle demnächst auslaufenden, nicht gekündigten Abos werden die fälligen
                  Folgerechnungen direkt ausgestellt. Offene Rechnungen werden nicht dupliziert.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={() => void handleGenerateRenewals()}>
                  Folgerechnungen erstellen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="size-4" /> Aktualisieren
          </Button>
        </div>
      </div>

      {settings && !settings.billingEnabled ? (
        <Card className="border-amber-300 bg-amber-50/70">
          <CardContent className="flex items-start gap-3 p-5">
            <ShieldAlert className="mt-0.5 size-5 shrink-0 text-amber-700" />
            <div>
              <p className="font-bold text-amber-950">Bezahlschranke ist noch deaktiviert</p>
              <p className="mt-1 text-sm leading-6 text-amber-900/80">
                Bankdaten und Rechnungsablauf sind vorbereitet. Bestehende kostenlose Zugriffe
                bleiben unverändert, bis die Liveschaltung bewusst erfolgt.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <section className="grid gap-4 sm:grid-cols-3">
        <Metric label="Zu prüfen" value={counts.draft} icon={<ReceiptText />} />
        <Metric label="Offen" value={counts.open} icon={<Landmark />} />
        <Metric label="Bezahlt" value={counts.paid} icon={<BadgeCheck />} />
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ReceiptText className="size-5 text-primary" /> Alle Rechnungen
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rechnung</TableHead>
                <TableHead>Kunde</TableHead>
                <TableHead>Produkt</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                    Noch keine Rechnungsanfragen vorhanden.
                  </TableCell>
                </TableRow>
              ) : null}
              {invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <p className="font-semibold">{invoice.invoiceNumber || "Neuer Entwurf"}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {new Date(invoice.createdAt).toLocaleString("de-CH")}
                    </p>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{invoice.customerCompany || invoice.customerName}</p>
                    <a className="text-xs text-primary" href={`mailto:${invoice.customerEmail}`}>
                      {invoice.customerEmail}
                    </a>
                  </TableCell>
                  <TableCell>
                    <p>{billingPlanName(invoice.planCode, "de")}</p>
                    {invoice.discountMinor > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {invoice.discountCode ?? "Aktion"}: −
                        {formatBillingPrice(invoice.discountMinor, "de-CH")} · Basis{" "}
                        {formatBillingPrice(invoice.subtotalMinor, "de-CH")}
                      </p>
                    ) : null}
                    {invoice.settlementKind === "discount" ? (
                      <p className="text-xs">Durch Rabatt ausgeglichen</p>
                    ) : null}
                    {invoice.servicePeriodStart && invoice.servicePeriodEnd ? (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {formatBillingPeriod(
                          invoice.servicePeriodStart,
                          invoice.servicePeriodEnd,
                          "de-CH",
                        )}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(invoice.status)}>
                      {statusLabels[invoice.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    {formatBillingPrice(invoice.amountMinor, "de-CH", invoice.currency)}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      {invoice.status === "draft" ? (
                        <Button
                          size="sm"
                          onClick={() => void handleIssue(invoice)}
                          disabled={busyId === invoice.id || !settings}
                        >
                          Ausstellen
                        </Button>
                      ) : null}
                      {invoice.invoiceNumber && settings ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            void downloadBillingInvoicePdf(
                              invoice,
                              settings,
                              billingPlanName(invoice.planCode, "de"),
                            ).catch((error) =>
                              toast.error(
                                error instanceof Error
                                  ? error.message
                                  : "PDF konnte nicht erstellt werden.",
                              ),
                            )
                          }
                        >
                          <Download className="size-4" /> PDF
                        </Button>
                      ) : null}
                      {invoice.creditNoteNumber && settings ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            void downloadBillingCreditNotePdf(
                              invoice,
                              settings,
                              billingPlanName(invoice.planCode, "de"),
                            ).catch((error) =>
                              toast.error(
                                error instanceof Error
                                  ? error.message
                                  : "Gutschrift konnte nicht erstellt werden.",
                              ),
                            )
                          }
                        >
                          <Download className="size-4" /> Gutschrift
                        </Button>
                      ) : null}
                      {invoice.status === "open" || invoice.status === "overdue" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => openPaymentDialog(invoice)}
                          disabled={busyId === invoice.id}
                        >
                          Zahlung bestätigen
                        </Button>
                      ) : null}
                      {invoice.status === "paid" && invoice.amountMinor > 0 ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openRefundDialog(invoice)}
                          disabled={busyId === invoice.id}
                        >
                          <Undo2 className="size-4" /> Rückerstatten
                        </Button>
                      ) : null}
                      {invoice.status === "draft" ||
                      invoice.status === "open" ||
                      invoice.status === "overdue" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            setVoidReason("");
                            setVoidInvoice(invoice);
                          }}
                          disabled={busyId === invoice.id}
                        >
                          <Ban className="size-4" /> Stornieren
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(paymentInvoice)}
        onOpenChange={(open) => !open && setPaymentInvoice(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zahlungseingang bestätigen</DialogTitle>
          </DialogHeader>
          {paymentInvoice ? (
            <div className="space-y-4">
              <div className="rounded-xl border bg-muted/30 p-4 text-sm">
                <p className="font-bold">{paymentInvoice.invoiceNumber}</p>
                <p className="mt-1 text-muted-foreground">
                  {paymentInvoice.customerCompany || paymentInvoice.customerName} ·{" "}
                  {formatBillingPrice(paymentInvoice.amountMinor, "de-CH", paymentInvoice.currency)}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment-received">Eingegangen am *</Label>
                <Input
                  id="payment-received"
                  type="datetime-local"
                  value={receivedAt}
                  onChange={(event) => setReceivedAt(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank-reference">Bankreferenz (optional)</Label>
                <Input
                  id="bank-reference"
                  value={bankReference}
                  onChange={(event) => setBankReference(event.target.value)}
                  placeholder="Buchungs-ID aus dem Kontoauszug"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment-notes">Interne Notiz (optional)</Label>
                <Textarea
                  id="payment-notes"
                  rows={3}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </div>
              <p className="text-xs leading-5 text-muted-foreground">
                Erst bestätigen, wenn der vollständige Betrag auf dem Bankkonto sichtbar ist. Die
                gebuchte Leistung wird anschliessend automatisch freigeschaltet.
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentInvoice(null)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => void handlePayment()}
              disabled={!paymentInvoice || !receivedAt || busyId === paymentInvoice?.id}
            >
              Eingang verbindlich bestätigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(voidInvoice)}
        onOpenChange={(open) => {
          if (!open) {
            setVoidInvoice(null);
            setVoidReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechnung stornieren</DialogTitle>
          </DialogHeader>
          {voidInvoice ? (
            <div className="space-y-4">
              <div className="rounded-xl border border-destructive/25 bg-destructive/5 p-4 text-sm">
                <p className="font-bold">{voidInvoice.invoiceNumber || "Rechnungsentwurf"}</p>
                <p className="mt-1 text-muted-foreground">
                  {voidInvoice.customerCompany || voidInvoice.customerName} ·{" "}
                  {formatBillingPrice(voidInvoice.amountMinor, "de-CH", voidInvoice.currency)}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="void-reason">Stornogrund *</Label>
                <Textarea
                  id="void-reason"
                  rows={3}
                  value={voidReason}
                  onChange={(event) => setVoidReason(event.target.value)}
                  placeholder="Zum Beispiel: Testrechnung oder Bestellung zurückgezogen"
                />
              </div>
              <p className="text-xs leading-5 text-muted-foreground">
                Die Rechnungsnummer bleibt für die Nachvollziehbarkeit erhalten. Der QR-Zahlteil
                wird im PDF als ungültig gekennzeichnet.
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidInvoice(null)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleVoid()}
              disabled={!voidInvoice || voidReason.trim().length < 3 || busyId === voidInvoice?.id}
            >
              Rechnung verbindlich stornieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(refundInvoice)}
        onOpenChange={(open) => !open && setRefundInvoice(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Vollständige Rückerstattung verbuchen</DialogTitle>
          </DialogHeader>
          {refundInvoice ? (
            <div className="space-y-4">
              <div className="rounded-xl border bg-muted/30 p-4 text-sm">
                <p className="font-bold">{refundInvoice.invoiceNumber}</p>
                <p className="mt-1 text-muted-foreground">
                  {formatBillingPrice(refundInvoice.amountMinor, "de-CH", refundInvoice.currency)}{" "}
                  wird vollständig gutgeschrieben.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="refund-date">Zurückgezahlt am *</Label>
                <Input
                  id="refund-date"
                  type="datetime-local"
                  value={refundedAt}
                  onChange={(event) => setRefundedAt(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="refund-reference">Bankreferenz (optional)</Label>
                <Input
                  id="refund-reference"
                  value={refundReference}
                  onChange={(event) => setRefundReference(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="refund-reason">Grund *</Label>
                <Textarea
                  id="refund-reason"
                  rows={3}
                  value={refundReason}
                  onChange={(event) => setRefundReason(event.target.value)}
                />
              </div>
              <p className="text-xs leading-5 text-muted-foreground">
                Erst bestätigen, nachdem die Rückzahlung bei der Bank ausgelöst wurde. Das System
                erstellt eine Gutschrift und beendet die zugehörige Leistung.
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundInvoice(null)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleRefund()}
              disabled={
                !refundInvoice ||
                !refundedAt ||
                refundReason.trim().length < 3 ||
                busyId === refundInvoice?.id
              }
            >
              Rückerstattung verbindlich verbuchen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function Metric({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 font-display text-3xl font-bold">{value}</p>
        </div>
        <span className="grid size-11 place-items-center rounded-xl bg-brand-soft text-brand-deep [&_svg]:size-5">
          {icon}
        </span>
      </CardContent>
    </Card>
  );
}
